'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useSession } from '@src/hooks/useSession'
import { useUnlocalizedPathname } from '@src/hooks/useUnlocalizedPathname'
import { useBreakpoints } from '@src/utils/breakpoints.util'

import type { TopNavItem, TopNavProps } from './TopNavProps'

interface TopNavContextType extends TopNavProps {
  expandedMenu: TopNavItem | null
  setExpandedMenu: (menu: TopNavItem | null) => void

  isExpanded: boolean
  setExpanded: (isExpanded: boolean) => void

  isMobileSubExpanded: boolean
  setMobileSubExpanded: (isExpanded: boolean) => void

  isAccountExpanded: boolean
  setAccountExpanded: (isExpanded: boolean) => void
}

export const TopNavContext = createContext<TopNavContextType>({
  expandedMenu: null,
  setExpandedMenu: () => {},

  isExpanded: false,
  setExpanded: () => {},

  isMobileSubExpanded: false,
  setMobileSubExpanded: () => {},

  isAccountExpanded: false,
  setAccountExpanded: () => {},
})

export const TopNavProvider = ({
  children,
  topNavProps,
}: {
  children: React.ReactNode
  topNavProps: TopNavProps
}) => {
  const { isComingFromLogin } = useSession()
  const pathname = useUnlocalizedPathname()
  const { isDesktop } = useBreakpoints()

  const [isExpanded, setExpanded] = useState(false)
  const [isMobileSubExpanded, setMobileSubExpanded] = useState(false)
  const [isAccountExpanded, setAccountExpanded] = useState(false)

  const [expandedMenu, setExpandedMenu] = useState<TopNavItem | null>(null)

  useEffect(() => {
    const shouldExpandAccount =
      isDesktop &&
      isComingFromLogin &&
      !pathname.startsWith('/account') &&
      !pathname.includes('/faq') &&
      !pathname.endsWith('/affiliate-application')
    setAccountExpanded(shouldExpandAccount)
  }, [isComingFromLogin, pathname, isDesktop])

  const value = useMemo(
    () => ({
      ...topNavProps,
      isExpanded,
      setExpanded,

      isMobileSubExpanded,
      setMobileSubExpanded,

      expandedMenu,
      setExpandedMenu,

      isAccountExpanded,
      setAccountExpanded,
    }),
    [topNavProps, isExpanded, isMobileSubExpanded, expandedMenu, isAccountExpanded]
  )

  return <TopNavContext.Provider value={value}>{children}</TopNavContext.Provider>
}

export const useTopNav = () => {
  const context = useContext(TopNavContext)
  if (!context) {
    throw new Error('useTopNav must be used within a TopNavProvider')
  }
  return context
}
